// DivisionGroup (round 2, a board composition): a division's lanes under one ruled heading row. Open, the
// heading is its fold toggle and name only: its lanes are right under it, so a count of them would say
// the same thing twice (concept A, 26 Oct); the group's name for screen readers still carries it. Folded,
// the lanes go, so the heading says what's in it ("3 technicians, 9 visits") and what's wrong, in red
// ("1 running late"), and holds each technician's day as a thin bar in their pen, so a folded division
// still shows its shape. With nobody in it, it says so ("Nobody yet: drag a technician here").
// While the board is being arranged (`edit`), the heading is ⋮⋮, which opens the heading's menu (move up,
// move down, remove), and the name as a field drawn as a ruled line: Enter or leaving it keeps the name,
// Escape puts it back. A heading can be where a dragged technician lands (`dropKey`, `isTarget`). The
// first group sits straight under the time scale, which already rules it. Reads only --division-*.
// Arranging (Amir, 26 Oct): the heading spans the whole row, across the time columns, like a section
// heading, so a long name is never cut at the names column; the field is as wide as its name. ⋮⋮ also
// drags the division (`onGripPointerDown`), its technicians with it: faded where it was (`lifted`), an
// ink mark over the heading it would land before (`slot`). A name too long for the heading comes cut
// at a word, with "…" (QA-767): its whole name is the group's name for screen readers and the heading's
// title. Its field says how many letters it may have and how many it has, as they're typed.
import { useId, useRef, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { Glyph } from '../Glyph/Glyph.tsx';
import type { MenuAt } from '../Menu/Menu.tsx';
import { penStyle, type Pen } from '../pen.ts';
import { spanHours, spanPct, useTimeSpan } from '../time-span.tsx';
import './DivisionGroup.css';

export interface DivisionDay {
  name: string;
  pen?: Pen | null;
  /** Their visits' times, for the folded bars; done ones are drawn hatched. */
  visits: { start: number; end: number; done?: boolean }[];
}

export interface DivisionEdit {
  /** The field's name for screen readers ("Name on the board for Drains"). */
  fieldLabel: string;
  value: string;
  onChange: (value: string) => void;
  /** Enter, or leaving the field. */
  onCommit?: () => void;
  /** The most letters the name may have (the server's limit); the field counts up to it. */
  maxLength?: number;
  /** Escape: the name as it was. */
  onCancel?: () => void;
  /** ⋮⋮'s name ("Reorder or remove Drains"); it opens the heading's menu. */
  gripLabel: string;
  onGripMenu: (at: MenuAt) => void;
  /** The grip's menu is open. */
  gripOpen?: boolean;
  /** A press on ⋮⋮: the start of dragging the division (the board moves it). */
  onGripPointerDown?: (e: PointerEvent<HTMLButtonElement>) => void;
}

export interface DivisionGroupProps {
  /** Its name as the heading draws it (cut at a word when it's long). */
  name: string;
  /** Its whole name, when `name` is cut: the heading's title and the group's name. */
  fullName?: string;
  /** "3 technicians, 9 visits": shown folded, or with nobody in it; always in its name for screen readers. */
  summary: string;
  /** What's wrong, in red on its own line, folded: "1 running late". */
  bad?: string | null;
  folded?: boolean;
  onToggle?: (folded: boolean) => void;
  /** Each technician's day, for the folded bars. */
  days?: DivisionDay[];
  /** The first group on the board: no rule above it. */
  first?: boolean;
  /** Nobody in it: the summary shows open too. */
  empty?: boolean;
  /** Arranging: ⋮⋮ and the name as a field. */
  edit?: DivisionEdit | null;
  /** Where a drag lands: the drop's key (the division's id), on the group, for the board to find. */
  dropKey?: string;
  /** A dragged technician would land here. */
  isTarget?: boolean;
  /** A dragged division would land just before this one: an ink mark over its heading. */
  slot?: boolean;
  /** This division is being dragged: faded where it was. */
  lifted?: boolean;
  /** Its lanes (BoardLane). */
  children: ReactNode;
}

/** The most letters a division's name has (businessUnits.arrangeDivisions' label). */
export const NAME_MAX = 40;

/** The narrowest the name's field draws while arranging, in characters. */
const FIELD_MIN_CH = 12;

export function DivisionGroup({
  name,
  fullName,
  summary,
  bad,
  folded = false,
  onToggle,
  days = [],
  first = false,
  empty = false,
  edit,
  dropKey,
  isTarget,
  slot,
  lifted,
  children,
}: DivisionGroupProps) {
  const span = useTimeSpan();
  const id = useId();
  const hours = spanHours(span).length - 1 || 1;
  const grip = useRef<HTMLButtonElement>(null);
  const showSummary = folded || empty;
  const max = edit?.maxLength ?? NAME_MAX;
  const length = edit ? edit.value.trim().replace(/\s+/g, ' ').length : 0;
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      edit?.onCommit?.();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      edit?.onCancel?.();
    }
  };
  // the keyboard's way to the heading's menu, as on a name: the menu key or Shift+F10 on ⋮⋮ or the field
  const menuKey = (e: KeyboardEvent<HTMLElement>) => {
    if (!edit || (e.key !== 'ContextMenu' && !(e.shiftKey && e.key === 'F10'))) return;
    e.preventDefault();
    if (grip.current) edit.onGripMenu({ element: grip.current });
  };
  const stop = (e: PointerEvent) => e.stopPropagation();
  return (
    <div
      className="fl-division"
      role="group"
      aria-label={`${fullName ?? name}, ${summary}`}
      data-first={first ? '' : undefined}
      data-drop={dropKey}
      data-lifted={lifted ? '' : undefined}
    >
      <div
        className="fl-division__row"
        data-folded={folded ? '' : undefined}
        data-slim={showSummary ? undefined : ''}
        data-editing={edit ? '' : undefined}
        data-target={isTarget ? '' : undefined}
        data-slot={slot ? '' : undefined}
      >
        <div className="fl-division__head">
          {edit ? (
            <div className="fl-division__edit" onKeyDown={menuKey}>
              <button
                ref={grip}
                type="button"
                className="fl-division__grip"
                aria-label={edit.gripLabel}
                aria-haspopup="menu"
                aria-expanded={edit.gripOpen ?? false}
                onPointerDown={(e) => {
                  edit.onGripPointerDown?.(e);
                  stop(e);
                }}
                onClick={(e) => edit.onGripMenu({ element: e.currentTarget })}
              >
                <Glyph name="grip" className="fl-division__grip-glyph" />
              </button>
              <input
                className="fl-division__input"
                aria-label={edit.fieldLabel}
                value={edit.value}
                autoComplete="off"
                spellCheck={false}
                // no hard stop (a paste kept its half word, QA-767): the count says the limit, and the
                // board keeps the whole words that fit
                aria-describedby={`${id}-count`}
                // as wide as its name (a few letters spare for typing), never wider than the limit: a
                // longer one scrolls inside the field, never the board under it (QA-767)
                size={Math.min(Math.max(edit.value.length + 2, FIELD_MIN_CH), max + 2)}
                onChange={(e) => edit.onChange(e.target.value)}
                onBlur={() => edit.onCommit?.()}
                onKeyDown={onKey}
              />
              {/* the limit, said as it's typed (QA-767): never a name cut short without a word */}
              <span
                id={`${id}-count`}
                className="fl-division__count"
                data-over={length > max ? '' : undefined}
              >
                {length > max
                  ? `${length} of ${max} letters: the words that fit are kept`
                  : `${length} of ${max} letters`}
              </span>
            </div>
          ) : (
            <button
              type="button"
              className="fl-division__toggle"
              aria-expanded={!folded}
              onClick={() => onToggle?.(!folded)}
            >
              <Glyph name="chevron-right" className="fl-division__glyph" />
              <span
                id={`${id}-name`}
                className="fl-division__name"
                {...(fullName && fullName !== name ? { title: fullName } : {})}
              >
                {name}
              </span>
            </button>
          )}
          {showSummary ? (
            <>
              <span className="fl-division__sub">{summary}</span>
              {bad ? <span className="fl-division__sub fl-division__bad">{bad}</span> : null}
            </>
          ) : null}
        </div>
        <div className="fl-division__track" style={{ backgroundSize: `${100 / hours}% 100%` }}>
          {folded
            ? days.map((d) => (
                <div
                  key={d.name}
                  className="fl-division__mini"
                  style={penStyle('division', d.pen)}
                  aria-hidden="true"
                >
                  {d.visits.map((v, i) => (
                    <span
                      key={i}
                      className="fl-division__bar"
                      data-done={v.done ? '' : undefined}
                      style={{
                        insetInlineStart: `${spanPct(span, v.start)}%`,
                        inlineSize: `${spanPct(span, v.end) - spanPct(span, v.start)}%`,
                      }}
                    />
                  ))}
                </div>
              ))
            : null}
        </div>
      </div>
      {folded ? null : children}
    </div>
  );
}
